// SystemBannerContext — shared state for the site-wide banner + its
// side effects (currently: disableUpgrades gates plan-upgrade CTAs).
//
// Fetches from GET /system/banner on mount, polls every 60s so admin
// changes propagate without a refresh (matches the API's 30s Cache-
// Control). Failure is silent — a broken banner endpoint should never
// crash the app or show a scary "banner unavailable" state.

import React from 'react';
import { fetchSystemBanner, SystemBanner } from '../services/systemBanner';

const DEFAULT: SystemBanner = { active: false };
const POLL_MS = 60_000;

const SystemBannerContext = React.createContext<SystemBanner>(DEFAULT);

export const SystemBannerProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const [banner, setBanner] = React.useState<SystemBanner>(DEFAULT);

  React.useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const b = await fetchSystemBanner();
      if (!cancelled) setBanner(b);
    };
    load();
    const id = window.setInterval(load, POLL_MS);
    return () => { cancelled = true; window.clearInterval(id); };
  }, []);

  return (
    <SystemBannerContext.Provider value={banner}>
      {children}
    </SystemBannerContext.Provider>
  );
};

/** Whole banner state — active + message + until + disableUpgrades. */
export const useSystemBanner = (): SystemBanner => React.useContext(SystemBannerContext);

/** Focused hook for upgrade CTAs — true when a system banner is active
 *  AND its disableUpgrades flag is set. Wraps the context so button
 *  code doesn't need to know the full shape. */
export const useUpgradesDisabled = (): boolean => {
  const b = React.useContext(SystemBannerContext);
  return b.active === true && b.disableUpgrades === true;
};
